import { isNumber } from 'lodash';
import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { flexbox, space, system } from 'styled-system';

import { ignoredProps } from '@hivebrite/design-system/Theme';

import { useGridContext } from 'hb-react/shared/components/DesignSystem/Grid/contexts/GridContext';
import type {
  ColInnerStyledProps,
  ColProps,
  ColStyledProps,
} from 'hb-react/shared/components/DesignSystem/Grid/types';
import { getBpNum } from 'hb-react/shared/components/DesignSystem/Grid/utils/getBpNum';

const getFlex = (width: string | number) =>
  width ? `0 0 ${getWidth(width)}` : 1;

const getWidth = (width: string | number) =>
  !isNumber(width) || width > 1 ? width : `${width * 100}%`;

export const Col = forwardRef<HTMLDivElement, ColProps>(
  (
    {
      children,
      growLeft,
      growRight,
      offset,
      overrideRowGap,
      width,
      ...otherProps
    },
    ref,
  ) => {
    const { colGap, columnsInMobile, gridWidth, numCols, rowGap } =
      useGridContext();

    const offsetMemoized = useMemo(
      () => (typeof offset === 'function' ? offset({ numCols }) : offset),
      [offset, numCols],
    );
    const widthMemoized = useMemo(
      () => (typeof width === 'function' ? width({ numCols }) : width),
      [width, numCols],
    );

    const clientWidth = Number.isNaN(document.documentElement?.clientWidth)
      ? 0
      : document.documentElement.clientWidth;

    const sideSpace = gridWidth
      ? (clientWidth - gridWidth) / 2
      : getBpNum(colGap, '_');

    return (
      <ColStyled
        $offset={offsetMemoized}
        width={widthMemoized}
        {...otherProps}
        ref={ref}
        tabIndex={-1}
      >
        <ColInnerStyled
          ml={
            growLeft
              ? {
                  _:
                    growLeft === 'pageBorder'
                      ? -sideSpace
                      : getBpNum(colGap, '_') * -1,
                }
              : undefined
          }
          mr={
            growRight
              ? {
                  _:
                    growRight === 'pageBorder'
                      ? -sideSpace
                      : getBpNum(colGap, '_') * -1,
                }
              : undefined
          }
          pl={{
            _: columnsInMobile ? getBpNum(colGap, '_') : 0,
            m: getBpNum(colGap, 'm'),
            l: getBpNum(colGap, 'l'),
            xl: getBpNum(colGap, 'xl'),
          }}
          pt={overrideRowGap ?? rowGap}
        >
          {children}
        </ColInnerStyled>
      </ColStyled>
    );
  },
);

export default Col;

const ColStyled = styled.div.withConfig({
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})<ColStyledProps>`
  ${flexbox}
  ${space}
  ${system({
    $offset: {
      property: 'marginLeft',
      transform: getWidth,
    },
    width: {
      property: 'flex',
      transform: (width) => (width === 'content' ? undefined : getFlex(width)),
    },
  })}

  ${system({
    width: {
      property: 'flexShrink',
      transform: (width) => (width === 'content' ? 0 : undefined),
    },
  })}
  min-width: 0;
  max-width: 100%;
`;

const ColInnerStyled = styled.div<ColInnerStyledProps>`
  ${space}

  height: 100%;
`;
